import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useCentralAssortmentFilters } from '@retail/my-assortment/context';
import { useFetchCentralAssortmentMemberView } from '@retail/my-assortment/data-access';
import { CentralAssortmentItem, isMainSupplierItem } from '@retail/my-assortment/types';
import { createItemIdentifiersPredicate } from '@retail/products/utils';
import { useMemo } from 'react';

export function useCentralAssortmentMemberViewItems() {
  const { assortmentId, appTenant } = useSelectedOrgUnit();
  const { selectedFilters } = useCentralAssortmentFilters();

  const {
    data: centralAssortmentDTO,
    isFetching,
    isError,
    error,
    refetch,
  } = useFetchCentralAssortmentMemberView({
    assortmentId,
    inAssortment:
      selectedFilters.inAssortment === 'UNSPECIFIED'
        ? undefined
        : selectedFilters.inAssortment === 'YES',
    supplierNumber: selectedFilters.supplierNumber
      ? Number(selectedFilters.supplierNumber)
      : undefined,
    groupNumbers: selectedFilters.productGroupNumbers,
    query: selectedFilters.searchQuery ?? '',
    fromDate: selectedFilters.validFrom,
    toDate: selectedFilters.validTo,
    tagIds: selectedFilters.subAssortmentIds.map((tagId) => Number(tagId)),
    suspense: false,
  });

  const itemIdentifiersPredicate = useMemo(
    () =>
      selectedFilters.itemIdentifiers
        ? createItemIdentifiersPredicate(selectedFilters.itemIdentifiers.identifiers)
        : undefined,
    [selectedFilters.itemIdentifiers]
  );

  const onlyMainSuppliers = appTenant.hasMainSupplier && selectedFilters.fromMainSupplier;

  const items = useMemo(() => {
    const allItems = centralAssortmentDTO
      ? centralAssortmentDTO.items.map(CentralAssortmentItem.fromDto)
      : [];
    return allItems.filter(
      (item) =>
        (!itemIdentifiersPredicate || itemIdentifiersPredicate(item)) &&
        (!onlyMainSuppliers || isMainSupplierItem(item))
    );
  }, [centralAssortmentDTO, itemIdentifiersPredicate, onlyMainSuppliers]);

  return {
    items,
    totalNumberOfItems: centralAssortmentDTO?.totalNumberOfItems ?? 0,
    foundNumberOfItems:
      itemIdentifiersPredicate || onlyMainSuppliers
        ? items.length
        : centralAssortmentDTO?.foundNumberOfItems,
    isFetching,
    isError,
    errorMessage: error?.payload?.error ?? error?.payload?.detail ?? error?.payload?.message,
    refetch,
  };
}
